import type { ComponentType, ReactNode } from "react";
import { RadioButton } from "../ui/radio_button";
import {
  alphabeticalSortOptions,
  allianceCountSortOptions,
  type AlphabeticalSortOption,
  type AllianceCountSortOption,
} from "../types";
import type { FreelancerSortModalMessages } from "../types/freelancer_modal_messages";

type ButtonComponent = ComponentType<{
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
}>;

type BlockingModalComponent = ComponentType<{
  className?: string;
  children: ReactNode;
}>;

export type FreelancerSortModalProps = {
  alphabeticalSort: AlphabeticalSortOption;
  allianceCountSort: AllianceCountSortOption;
  className?: string;
  isBlocking?: boolean;
  onClose: () => void;
  setAlphabeticalSort: (sort: AlphabeticalSortOption) => void;
  setAllianceCountSort: (sort: AllianceCountSortOption) => void;
  onSubmit?: () => void;
  messages: FreelancerSortModalMessages;
  Button: ButtonComponent;
  FullBlockingModal: BlockingModalComponent;
};

export function FreelancerSortModal({
  alphabeticalSort,
  allianceCountSort,
  className,
  isBlocking = true,
  onClose,
  setAlphabeticalSort,
  setAllianceCountSort,
  onSubmit,
  messages,
  Button,
  FullBlockingModal,
}: FreelancerSortModalProps) {
  const TopDiv = isBlocking ? FullBlockingModal : "div";

  return (
    <TopDiv className={className}>
      <h3 className="mb-4 text-secondary md:hidden">{messages.sortBy}</h3>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <div>
            <label htmlFor="alphabeticalSort" className="text-secondary">
              {messages.alphabetical}
            </label>

            <div className="my-4 flex gap-4">
              {alphabeticalSortOptions.map((option) => (
                <RadioButton
                  key={option}
                  name="alphabeticalSort"
                  checked={alphabeticalSort === option}
                  onChange={() => {
                    setAlphabeticalSort(option);
                  }}
                  label={messages.getSortLabel(option)}
                  value={option}
                />
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="allianceCountSort" className="text-secondary">
              {messages.allianceCount}
            </label>

            <div className="my-4 flex gap-4">
              {allianceCountSortOptions.map((option) => (
                <RadioButton
                  key={option}
                  name="allianceCountSort"
                  checked={allianceCountSort === option}
                  onChange={() => {
                    setAllianceCountSort(option);
                  }}
                  label={messages.getSortLabel(option)}
                  value={option}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button
            onClick={() => onSubmit?.()}
            className="mt-4 w-full md:mt-0 md:w-auto md:px-8"
          >
            {messages.showResults}
          </Button>

          <button
            type="button"
            className="mx-auto block font-bold leading-tight text-secondary underline md:mx-16"
            onClick={onClose}
          >
            {messages.close}
          </button>
        </div>
      </div>
    </TopDiv>
  );
}
